import axios, {
  type AxiosRequestConfig,
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import { tokenUtilsV2, authUtils } from "../utils/auth";
import { API_CONFIG } from "../config/constants";

/**
 * Generic API service for making HTTP requests
 *
 * Provides a centralized way to handle all API calls with consistent error handling,
 * request/response transformation, and configuration.
 *
 * Features:
 * - Automatic token attachment for authenticated requests
 * - Automatic token refresh on 401 errors (when refresh token is available)
 * - Request queuing during token refresh to prevent race conditions
 * - Support for both authenticated and public endpoints
 * - Graceful handling of missing tokens (requests proceed without auth header)
 */
class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  constructor() {
    /**
     * Type guard to check if headers object has a 'set' method
     * Used for type-safe header manipulation
     */
    const hasHeaderSetter = (
      headers: unknown
    ): headers is { set: (name: string, value: string) => void } => {
      return (
        !!headers && typeof (headers as { set?: unknown }).set === "function"
      );
    };

    // API configuration
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    /**
     * Request interceptor to add auth headers
     *
     * Automatically attaches the access token to requests if available.
     * If no token exists, the request proceeds without the Authorization header,
     * allowing public endpoints to work seamlessly.
     */
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = tokenUtilsV2.getAccessToken();

        // Only attach token if it exists - allows public endpoints to work
        if (token) {
          const { headers } = config;
          if (hasHeaderSetter(headers)) {
            headers.set("Authorization", `Bearer ${token}`);
          } else {
            const plainHeaders =
              (headers as Record<string, string> | undefined) ?? {};
            config.headers = {
              ...plainHeaders,
              Authorization: `Bearer ${token}`,
            } as unknown as InternalAxiosRequestConfig["headers"];
          }
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    /**
     * Response interceptor to handle token refresh
     *
     * Handles 401 Unauthorized errors by attempting to refresh the access token.
     * Implements request queuing to prevent multiple simultaneous refresh attempts.
     *
     * Behavior:
     * - If 401 error occurs and user was authenticated (has refresh token):
     *   Attempts to refresh the token and retry the original request
     * - If 401 error occurs and user was NOT authenticated (no refresh token):
     *   Simply rejects the error without redirecting (allows public endpoints to return 401)
     * - If token refresh fails: Clears auth state and redirects to login
     */
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = (error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        })!;

        // Only attempt token refresh on 401 errors and if we haven't already tried
        if (error.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = tokenUtilsV2.getRefreshToken();

          /**
           * If no refresh token exists, this was either:
           * 1. A public endpoint that returned 401 (e.g., wrong credentials on login)
           * 2. An authenticated endpoint called without being logged in
           *
           * In either case, we don't redirect - just return the error.
           * This allows components to handle 401s appropriately (e.g., show login form)
           */
          if (!refreshToken) {
            return Promise.reject(error);
          }

          /**
           * If a token refresh is already in progress, queue this request
           * to be retried once the refresh completes
           */
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                const { headers } = originalRequest;
                if (hasHeaderSetter(headers)) {
                  headers.set("Authorization", `Bearer ${token}`);
                } else {
                  const plainHeaders =
                    (headers as Record<string, string> | undefined) ?? {};
                  originalRequest.headers = {
                    ...plainHeaders,
                    Authorization: `Bearer ${token}`,
                  } as unknown as InternalAxiosRequestConfig["headers"];
                }
                return this.api(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          // Mark this request as a retry to prevent infinite loops
          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            /**
             * Attempt to refresh the access token
             *
             * Note: Using a separate axios instance here to avoid triggering
             * the interceptor on the refresh request itself
             */
            const response = await axios.post<{
              accessToken: string;
              refreshToken?: string;
            }>(`${API_CONFIG.BASE_URL}/auth/refresh`, {
              refreshToken,
            });

            const { accessToken, refreshToken: newRefreshToken } =
              response.data;

            if (accessToken) {
              // Store new tokens (use new refresh token if provided, otherwise keep the old one)
              tokenUtilsV2.storeTokens({
                accessToken,
                refreshToken: newRefreshToken || refreshToken,
              });

              // Update default authorization header for future requests
              this.api.defaults.headers.common["Authorization"] =
                `Bearer ${accessToken}`;

              // Update the original request with the new token
              const { headers } = originalRequest;
              if (hasHeaderSetter(headers)) {
                headers.set("Authorization", `Bearer ${accessToken}`);
              } else {
                const plainHeaders =
                  (headers as Record<string, string> | undefined) ?? {};
                originalRequest.headers = {
                  ...plainHeaders,
                  Authorization: `Bearer ${accessToken}`,
                } as unknown as InternalAxiosRequestConfig["headers"];
              }

              // Process all queued requests with the new token
              this.processQueue(null, accessToken);

              // Retry the original request with the new token
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            /**
             * Token refresh failed - this means the refresh token is invalid/expired
             * Clear all auth state and redirect to login page
             */
            this.processQueue(refreshError as Error, null);
            authUtils.clearAuth();

            // Only redirect if we're not already on the login page
            if (window.location.pathname !== "/login") {
              window.location.href = "/login";
            }

            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // For all other errors (not 401 or already retried), just reject
        return Promise.reject(error);
      }
    );
  }

  /**
   * Process queued requests after token refresh
   *
   * @param error - Error object if token refresh failed, null if successful
   * @param token - New access token if refresh was successful, null if failed
   *
   * This method resolves or rejects all requests that were queued while
   * a token refresh was in progress.
   */
  private processQueue(error: Error | null, token: string | null = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  /**
   * Generic GET method
   *
   * @param url - API endpoint URL
   * @param config - Optional axios configuration
   * @returns Promise<T> - Response data
   *
   * Usage:
   * - Public endpoint: await apiService.get('/public/data')
   * - Authenticated endpoint: await apiService.get('/user/profile')
   *   (token automatically attached if user is logged in)
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get(url, config);
    return response.data;
  }

  /**
   * Generic POST method
   *
   * @param url - API endpoint URL
   * @param data - Request body data
   * @param config - Optional axios configuration
   * @returns Promise<T> - Response data
   *
   * Usage:
   * - Public endpoint: await apiService.post('/auth/login', credentials)
   * - Authenticated endpoint: await apiService.post('/user/update', data)
   */
  async post<T, D = Record<string, unknown>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.api.post(url, data, config);
    return response.data;
  }

  /**
   * Generic PUT method
   *
   * @param url - API endpoint URL
   * @param data - Request body data
   * @param config - Optional axios configuration
   * @returns Promise<T> - Response data
   */
  async put<T, D = Record<string, unknown>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.api.put(url, data, config);
    return response.data;
  }

  /**
   * Generic DELETE method
   *
   * @param url - API endpoint URL
   * @param config - Optional axios configuration
   * @returns Promise<T> - Response data
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete(url, config);
    return response.data;
  }

  /**
   * Generic PATCH method
   *
   * @param url - API endpoint URL
   * @param data - Request body data
   * @param config - Optional axios configuration
   * @returns Promise<T> - Response data
   */
  async patch<T, D = Record<string, unknown>>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.api.patch(url, data, config);
    return response.data;
  }

  /**
   * Get the axios instance for advanced use cases
   *
   * @returns AxiosInstance - The underlying axios instance
   *
   * Use this when you need direct access to axios features not exposed
   * by the generic methods (e.g., custom interceptors, cancelation tokens)
   */
  getAxiosInstance(): AxiosInstance {
    return this.api;
  }
}

export const apiService = new ApiService();
